import { Roles } from "@/utilities/types/Users.ts";
import { IFloor, IFloorName, IUnitTypeFloor } from "@/utilities/types/Floor.ts";
import { HeaderCell } from "@/components/ui/table.tsx";
import useProfile from "@/hooks/use-profile.ts";
import DeletePopover from "@/app/shared/delete-popover.tsx";
import ModalCreateEditFloor from "@/app/app-shared/floor-plan/create-edit/modal-edit.tsx";
import { ActionIcon, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { routes } from "@/config/routes";
import { BsBoxes } from "react-icons/bs";
import { getParamsCurrentPage } from "@/utilities/functions";
import { SortConfig } from "@/hooks/useTableServer";

type FloorSortBy =
  | "name"
  | "unitType"
  | "area"
  | "minPrice"
  | "maxPrice"
  | "totalUnits";

type Columns = {
  data: IFloor[];
  onDeleteItem?: (id: string) => void;
  onHeaderCellClick: (value: string) => void;
  onUpdateFloor?: (id: string) => Promise<void>;
  reset?: () => void;
  role: Roles;
  projectId: string;
  refresh?: () => void;
  sortConfig: SortConfig<FloorSortBy>;
};
export const GetColumns = ({
  data,
  onHeaderCellClick,
  onDeleteItem,
  role,
  reset,
  projectId,
  refresh,
  sortConfig,
}: Columns) => [
  {
    title: (
      <HeaderCell
        title="Name"
        sortable
        align="left"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "name"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("name"),
    dataIndex: "name",
    key: "name",
    render: (name: IFloorName) => {
      return <>{typeof name === "object" ? name?.en : name}</>;
    },
  },
  {
    title: (
      <HeaderCell
        title="Unit Type"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "unitType"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("unitType"),
    dataIndex: "unitType",
    key: "unitType",
    render: (unitType: IUnitTypeFloor) => {
      return (
        <span className="flex items-center justify-center">
          {typeof unitType?.title === "object"
            ? unitType?.title?.en
            : unitType?.title}
        </span>
      );
    },
  },
  {
    title: (
      <HeaderCell
        title="Size(sqft)"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "area"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("area"),
    dataIndex: "area",
    key: "area",
    render: (area: IFloor["area"]) => {
      return <span className="flex items-center justify-center">{area}</span>;
    },
  },
  {
    title: (
      <HeaderCell
        title="Min Price"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "minPrice"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("minPrice"),
    dataIndex: "minPrice",
    key: "minPrice",
    render: (minPrice: IFloor["minPrice"]) => {
      return (
        <span className="flex items-center justify-center">{minPrice}</span>
      );
    },
  },
  {
    title: (
      <HeaderCell
        title="Max Price"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "maxPrice"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("maxPrice"),
    dataIndex: "maxPrice",
    key: "maxPrice",
    render: (maxPrice: IFloor["maxPrice"]) => {
      return (
        <span className="flex items-center justify-center">{maxPrice}</span>
      );
    },
  },
  {
    title: (
      <HeaderCell
        title="Total Units"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "totalUnits"
        }
      />
    ),
    onHeaderCell: () => onHeaderCellClick("totalUnits"),
    dataIndex: "totalUnits",
    key: "totalUnits",
    render: (_, row: IFloor) => {
      return (
        <span className="flex items-center justify-center">
          {row.availableUnits}/{row.totalUnits}
        </span>
      );
    },
  },
  {
    title: (
      <HeaderCell
        className="ps-3"
        title={<span className="whitespace-nowrap">Actions</span>}
      />
    ),
    dataIndex: "action",
    key: "action",
    width: 120,
    render: (_: string, row: IFloor) => {
      return (
        <ActionAdmin
          onDeleteItem={() => onDeleteItem?.(row.id)}
          row={row}
          projectId={projectId}
          reset={reset}
          refresh={refresh}
        />
      );
    },
  },
];

const ActionAdmin = ({
  onDeleteItem,
  row,
  projectId,
  reset,
  refresh,
}: {
  onDeleteItem: () => void;
  reset?: () => void;
  onUpdateFloor?: () => Promise<void>;
  row: IFloor;
  projectId: string;
  refresh?: () => void;
}) => {
  const { profile } = useProfile();
  return (
    <div className="flex items-center justify-center gap-3 pe-4">
      {profile?.role === Roles.ADMIN && (
        <>
          <ModalCreateEditFloor
            row={row}
            projectId={projectId}
            reset={reset}
            refresh={refresh}
          />
          <DeletePopover
            title={`Delete Floorplan`}
            description={`Are you sure to delete this Floorplan?`}
            onDelete={onDeleteItem}
          />
        </>
      )}
      <Tooltip size="sm" content={"View Units"} placement="top" color="invert">
        <Link
          to={{
            pathname: `${routes.unit.listing(projectId || "", row?.id)}`,
            search: getParamsCurrentPage({ rowKey: row?.id }),
          }}
        >
          <ActionIcon size="sm" variant="outline">
            <BsBoxes className="h-4 w-4" />
          </ActionIcon>
        </Link>
      </Tooltip>
    </div>
  );
};
